<template>
  <div class="pay-succ">
    <IconSuccess class="pay-succ__icon" />
    <div class="pay-succ__title">{{ $t('zhi-fu-cheng-gong') }}</div>
    <div class="pay-succ__subtitle">{{ title }}</div>
    <div class="pay-succ__desc">{{ desc }}</div>
    <div v-if="showPullNewInfo" class="pay-succ__award">
      <div v-if="award.accept_total_credit_coins" class="pay-succ__award-item">
        {{ acceptAwardText }}
      </div>
      <div v-if="award.invitation_total_credit_coins" class="pay-succ__award-item">
        <span v-if="!award.accept_total_credit_coins">{{
          $t('nin-yi-wan-cheng-hao-you-zhu-li')
        }}</span>
        {{ invitationAwardText }}
      </div>
      <div class="pay-succ__award-item">
        {{ $t('kuai-lai-ling-qu-ni-de') }}
        <a class="pay-succ__award-link" @click="onClickInviteLink">
          {{ $t('zhuan-shu-yao-qing-lian-jie') }}
        </a>
        ，{{ $t('yao-qing-hao-you-gong-xiang-quan-yi-ba') }}
      </div>
    </div>
    <div class="pay-succ__countdown">
      <span>{{ $t('secondss-hou-shua-xin-ye-mian', [seconds]) }}</span>
      <span v-if="tip">，{{ tip }}</span>
    </div>
  </div>
</template>

<script setup lang="ts" name="PaySucc">
import {
  getInviteRecordInfo,
  INVITE_RECORD_TYPE,
  readRecordType,
} from '@rendora/common/entities/attraction';
import { getI18nService } from '@rendora/common/services/i18n';
import { useUserStore } from '@rendora/common/store/user';
import { getPlatformJumpLink } from '@rendora/common/utils/url';
import { localSetItem } from '@rendora/common/utils/webStorage';
import IconSuccess from '@rendora/design/assets/circle-success.svg?component';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

interface AwardInfo {
  accept_total_credit_coins: number;
  invitation_total_credit_coins: number;
  read: boolean; // 是否已经查看过奖励信息
}

const props = withDefaults(
  defineProps<{
    title?: string;
    desc?: string;
    tip?: string;
    /** 父组件已处理刷新时设为 true，避免重复 location.reload */
    skipReload?: boolean;
  }>(),
  { skipReload: false },
);

const emits = defineEmits<{
  onSuccess: [];
}>();

const $t = getI18nService().t;
const userStore = useUserStore();

const isInviteUser = ref(false);
const award = ref<AwardInfo>({
  accept_total_credit_coins: 0,
  invitation_total_credit_coins: 0,
  read: false,
});
const timer = ref<ReturnType<typeof setInterval> | null>(null);
const seconds = ref(5);

const showPullNewInfo = computed(
  () =>
    isInviteUser.value &&
    (award.value.accept_total_credit_coins > 0 || award.value.invitation_total_credit_coins > 0) &&
    !award.value.read,
);

const acceptAwardText = computed(() => {
  const value = award.value.accept_total_credit_coins;
  if (!value) return '';
  return $t(
    'nin-yi-wan-cheng-hao-you-zhu-li-jiang-li-nin-invitepropsawardaccepttotalcreditcoins-yan-bi',
    [value],
  );
});

const invitationAwardText = computed(() => {
  const value = award.value.invitation_total_credit_coins;
  if (!value) return '';
  return $t('nin-de-hao-you-huo-de-invitepropsawardinvitationtotalcreditcoins-yan-bi', [value]);
});

const INVITE_INFO_TIMEOUT_MS = 3000;

const getInviteInfo = async () => {
  try {
    const res = await Promise.race([
      getInviteRecordInfo({ many: true, is_invitee: 0 }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), INVITE_INFO_TIMEOUT_MS),
      ),
    ]);
    isInviteUser.value = Boolean(res.data?.length);
    award.value = {
      accept_total_credit_coins: res.data?.[0]?.accept_total_credit_coins ?? 0,
      invitation_total_credit_coins: res.data?.[0]?.invitation_total_credit_coins ?? 0,
      read: res.data?.[0]?.accept_read ?? false,
    };
    if (res.data?.length > 1) {
      const item = res.data?.find(o => o.goon_behavior_type != INVITE_RECORD_TYPE.REGISTER);
      // 首次购买后邀请奖励设为已读
      if (item && !item.accept_read) {
        await readRecordType(0, { record_id: item.id });
      }
    }
  } catch (error) {
    if ((error as Error)?.message !== 'timeout') {
      console.error('获取邀请信息失败:', error);
    }
  }
};

const onClickInviteLink = () => {
  location.href = getPlatformJumpLink('/pull-new');
};

const clearTimer = () => {
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
};

const startCountdown = () => {
  localSetItem('update_userinfo', Date.now());
  timer.value = setInterval(() => {
    // 仅在倒计时最后一秒同步一次用户信息，避免频繁请求
    if (seconds.value === 1) {
      userStore.fetchUserInfo();
      localSetItem('update_userinfo', Date.now());
    }

    if (seconds.value <= 0) {
      clearTimer();
      emits('onSuccess');
      if (!props.skipReload) {
        location.reload();
      }
      return;
    }
    seconds.value--;
  }, 1000);
};

onMounted(async () => {
  await getInviteInfo();
  startCountdown();
});

onBeforeUnmount(() => {
  clearTimer();
});
</script>

<style lang="scss" scoped>
.pay-succ__icon {
  color: var(--x-success-6);
  width: 40px;
  height: 40px;
}

.pay-succ {
  min-width: 800px;
  padding: 30px;
  min-height: 290px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.pay-succ__title {
  margin-top: 12px;
  font-size: 16px;
  font-weight: bold;
}

.pay-succ__subtitle {
  font-size: 40px;
  font-weight: 400;
  color: var(--x-text-1);
}

.pay-succ__desc,
.pay-succ__countdown {
  font-size: 14px;
  line-height: 20px;
  color: var(--x-warning-8);
}

.pay-succ__award {
  font-size: 14px;
  line-height: 20px;
  color: var(--x-text-3);
  text-align: center;
  padding: 24px 0 0;
}

.pay-succ__award-item {
  margin-bottom: 8px;
}

.pay-succ__award-link {
  color: var(--x-purple-6);
  cursor: pointer;
}
</style>
